import type { components } from "@/api/schema";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import {
	Chip,
	RadioGroup,
	RadioGroupItem,
	Typography,
} from "@st-marketplace/ui";
import { useId } from "react";

export type ExecutionPlanVersion =
	components["schemas"]["ExecutionPlanVersion"];

/** v2 first: it is the default and the recommended planner. */
const VERSIONS = ["v2", "v1"] as const satisfies ExecutionPlanVersion[];

type Props = {
	value: ExecutionPlanVersion;
	onChange: (value: ExecutionPlanVersion) => void;
	disabled?: boolean;
	className?: string;
	"aria-labelledby"?: string;
};

/** Radio cards: two exclusive options that each need a line of explanation. */
export const ExecutionPlanVersionChoice: React.FC<Props> = ({
	value,
	onChange,
	disabled,
	className,
	"aria-labelledby": labelledBy,
}) => {
	const { t } = useTranslation();
	const id = useId();

	return (
		<RadioGroup
			value={value}
			onValueChange={(next) => onChange(next as ExecutionPlanVersion)}
			disabled={disabled}
			aria-labelledby={labelledBy}
			className={cn("grid gap-2", className)}
		>
			{VERSIONS.map((version) => {
				const selected = value === version;
				return (
					<label
						key={version}
						htmlFor={`${id}-${version}`}
						className={cn(
							"flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors",
							"has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary-border",
							selected
								? "border-primary-border bg-primary-fill/10"
								: "border-secondary-border bg-background-100 hover:bg-background-300",
							disabled && "cursor-not-allowed opacity-60",
						)}
					>
						<RadioGroupItem
							id={`${id}-${version}`}
							value={version}
							className="mt-0.5 shrink-0"
						/>
						<span className="flex min-w-0 flex-col gap-1">
							<span className="flex flex-wrap items-center gap-2">
								<Typography variant="body-sm" as="span" className="font-medium">
									{t(`executionPlanVersion.${version}.label`)}
								</Typography>
								{version === "v2" && (
									<Chip variant="success" size="sm">
										{t("executionPlanVersion.recommended")}
									</Chip>
								)}
							</span>
							<Typography
								variant="caption"
								as="span"
								className="leading-5 text-secondary-text-100"
							>
								{t(`executionPlanVersion.${version}.description`)}
							</Typography>
						</span>
					</label>
				);
			})}
		</RadioGroup>
	);
};
